/**
 * Subscription Store
 *
 * Zustand store for managing Stripe subscription state.
 * Syncs with the backend API to track subscription status.
 */

import { create } from 'zustand';
import { useAuthStore } from './authStore';
import {
  getSubscription,
  createCheckoutSession,
  getCustomerPortalUrl,
  isSubscriptionActive,
  isInGracePeriod,
  getGracePeriodDaysRemaining,
  type Subscription,
  type PricingPlan,
} from '../services/subscriptionService';

/**
 * Track subscription analytics event from store (non-React context).
 * Fire-and-forget pattern - analytics errors are silently ignored.
 */
function trackSubscriptionEvent(eventName: string): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;

  window.electronAPI.analytics.track(eventName, {}).catch(() => {
    // Silently ignore analytics errors
  });
}

interface SubscriptionState {
  subscription: Subscription | null;
  isLoading: boolean;
  isCheckingOut: boolean;
  error: string | null;
  /** True after the first successful fetch - prevents false "upgrade" events on initial load */
  hasLoadedOnce: boolean;
  /** True when user appears signed in but entitlement fetch fails due to auth — zombie state */
  authStale: boolean;
}

interface SubscriptionActions {
  fetchSubscription: () => Promise<void>;
  startCheckout: (plan: PricingPlan) => Promise<{ success: boolean; url?: string; error?: string; errorCode?: string }>;
  openCustomerPortal: () => Promise<{ success: boolean; url?: string; error?: string }>;
  clearError: () => void;
  /** Mark/clear the zombie-auth state (session present but not refreshable). */
  markAuthStale: (stale: boolean) => void;
  reset: () => void;
}

type SubscriptionStore = SubscriptionState & SubscriptionActions;

const initialState: SubscriptionState = {
  subscription: null,
  isLoading: false,
  isCheckingOut: false,
  error: null,
  hasLoadedOnce: false,
  authStale: false,
};

export const useSubscriptionStore = create<SubscriptionStore>((set, get) => ({
  ...initialState,

  fetchSubscription: async () => {
    const { subscription: previousSubscription, hasLoadedOnce } = get();
    const wasActive = isSubscriptionActive(previousSubscription);

    set({ isLoading: true, error: null });

    let result = await getSubscription();

    // Retry once after a short delay when auth fails.
    // Covers transient token refresh failures (e.g. returning from Stripe
    // checkout while the access token expired and refresh needs a moment).
    if (!result.success && result.error?.includes('Not authenticated')) {
      // Fast path: on a cold-start auth race, the main process usually already
      // holds a valid offline entitlement (signed JWT cache). Resolve from it
      // immediately so `hasLoadedOnce` flips and Pro-gated UI (e.g. the
      // "Loading sessions…" spinner) clears without waiting on the 2s retry.
      // The online fetch is still retried below to reconcile fresh data.
      try {
        const cached = await window.electronAPI?.entitlement?.get();
        if (cached?.isPro && cached.source !== 'pro-build') {
          // Trial entitlement is account-scoped: ownership is verified by the
          // license store (trialService checks trialUserId against the
          // signed-in user). The main-process entitlement is date-only and
          // account-blind, so synthesizing a "trialing" subscription here
          // would bypass the account check and leak Pro to a different user.
          // Just clear the loading gate — trial-based Pro flows through the
          // account-aware licenseStore path, not the subscription store.
          if (cached.source === 'trial') {
            set({ isLoading: false, hasLoadedOnce: true });
            return;
          }
          console.log(
            '[SubscriptionStore] Auth race — resolving from offline entitlement:',
            cached.source
          );
          set({
            subscription: {
              id: '',
              userId: '',
              stripeCustomerId: '',
              stripeSubscriptionId: null,
              status: 'active' as const,
              priceId: '',
              currentPeriodStart: null,
              currentPeriodEnd: null,
              cancelAtPeriodEnd: false,
            },
            isLoading: false,
            hasLoadedOnce: true,
          });
          return;
        }
      } catch {
        // Fall through to the delayed retry below.
      }

      console.log('[SubscriptionStore] Auth failed, retrying in 2s...');
      await new Promise((r) => setTimeout(r, 2000));
      result = await getSubscription();
    }

    if (result.success && result.subscription) {
      const sub = result.subscription;

      // Update offline license cache with fresh subscription data
      try {
        await window.electronAPI?.licenseCache?.updateCache(
          sub.status,
          sub.currentPeriodEnd?.toISOString(),
          sub.userId,
          undefined // userEmail not available here
        );
      } catch (e) {
        console.error('Failed to update license cache:', e);
      }

      set({
        subscription: sub,
        isLoading: false,
        hasLoadedOnce: true,
        authStale: false,
      });

      // Track subscription upgrade if transitioning from non-active to active
      // Only track if we've loaded before - prevents false events on initial app load
      const isNowActive = isSubscriptionActive(sub);
      if (hasLoadedOnce && isNowActive && !wasActive) {
        trackSubscriptionEvent('subscription:upgrade');
      }
    } else {
      // Online fetch failed — fall back to main process entitlement.
      // The main process has access to the offline cache (signed JWT token)
      // which can grant Pro even when the online fetch fails (e.g. auth token
      // expired, network error). Without this fallback the renderer shows
      // free mode while the main process correctly says "Pro".
      try {
        const entitlement = await window.electronAPI?.entitlement?.get();
        if (entitlement?.isPro && entitlement.source !== 'pro-build') {
          // See the auth-race fast path above: trial entitlement is
          // account-scoped and must not be laundered into a subscription,
          // which would bypass the account-based trial ownership check.
          if (entitlement.source === 'trial') {
            set({ isLoading: false, hasLoadedOnce: true });
            return;
          }
          console.log('[SubscriptionStore] Online fetch failed, using main process entitlement:', entitlement.source);
          set({
            subscription: {
              id: '',
              userId: '',
              stripeCustomerId: '',
              stripeSubscriptionId: null,
              status: 'active' as const,
              priceId: '',
              currentPeriodStart: null,
              currentPeriodEnd: null,
              cancelAtPeriodEnd: false,
            },
            isLoading: false,
            hasLoadedOnce: true,
          });
          return;
        }
      } catch (e) {
        console.warn('[SubscriptionStore] Main process entitlement fallback failed:', e);
      }

      // Detect zombie auth state: user appears signed in but API calls fail.
      // This prompts the user to re-sign-in to restore their subscription.
      const isAuthError = result.error?.includes('Not authenticated') ?? false;
      const isUserSignedIn = Boolean(useAuthStore.getState().user);
      const isZombieAuth = isAuthError && isUserSignedIn;

      // Don't surface "Not authenticated" as an error in the UI.
      // This can happen on startup before auth hydration completes.
      const shouldShowError = Boolean(
        result.error && !result.error.startsWith('Not authenticated')
      );

      set({
        isLoading: false,
        hasLoadedOnce: true,
        authStale: isZombieAuth,
        error: shouldShowError ? (result.error || 'Failed to fetch subscription') : null,
      });
    }
  },

  startCheckout: async (plan: PricingPlan) => {
    if (get().isCheckingOut) {
      return { success: false };
    }

    set({ isCheckingOut: true, error: null });

    const result = await createCheckoutSession(plan);

    if (result.success && result.url) {
      // Open checkout URL in default browser
      window.open(result.url, '_blank', 'noopener,noreferrer');
      set({ isCheckingOut: false });
      return { success: true, url: result.url };
    } else {
      const isAuthError = result.error?.includes('Not authenticated') ?? false;
      const isUserSignedIn = Boolean(useAuthStore.getState().user);
      set({
        isCheckingOut: false,
        authStale: isAuthError && isUserSignedIn ? true : get().authStale,
        error: (isAuthError && isUserSignedIn) ? null : (result.error || 'Failed to create checkout session'),
      });
      return { success: false, error: result.error, errorCode: result.errorCode };
    }
  },

  openCustomerPortal: async () => {
    const result = await getCustomerPortalUrl();

    if (result.success && result.url) {
      // Open portal URL in default browser
      window.open(result.url, '_blank', 'noopener,noreferrer');
      return { success: true, url: result.url };
    } else {
      // Detect zombie auth: user appears signed in but token is stale
      const isAuthError = result.error?.includes('Not authenticated') ?? false;
      const isUserSignedIn = Boolean(useAuthStore.getState().user);
      if (isAuthError && isUserSignedIn) {
        set({ authStale: true, error: null });
      } else {
        set({ error: result.error || 'Failed to open customer portal' });
      }
      return { success: false, error: result.error };
    }
  },

  clearError: () => set({ error: null }),

  markAuthStale: (stale: boolean) => set({ authStale: stale }),

  reset: () => set(initialState),
}));

// Selectors
export const selectSubscription = (state: SubscriptionStore) => state.subscription;
export const selectIsLoading = (state: SubscriptionStore) => state.isLoading;
export const selectIsCheckingOut = (state: SubscriptionStore) => state.isCheckingOut;
export const selectError = (state: SubscriptionStore) => state.error;

export const selectIsPro = (state: SubscriptionStore) =>
  isSubscriptionActive(state.subscription);

export const selectIsInGracePeriod = (state: SubscriptionStore) =>
  isInGracePeriod(state.subscription);

export const selectGracePeriodDaysRemaining = (state: SubscriptionStore) =>
  getGracePeriodDaysRemaining(state.subscription);

export const selectSubscriptionStatus = (state: SubscriptionStore) =>
  state.subscription?.status || 'none';

export const selectAuthStale = (state: SubscriptionStore) =>
  state.authStale;
